// Operator › Agent health (SKILLSMITH-HUB, founder 2026-10-30): platform super admins only (the
// API answers 404 to anyone else). A left list of topics, one per company; the main pane is the
// morning view ("Since yesterday": every company's new Skillsmith reports, critical first, with
// counts, filters and Mark reviewed) or one company's topic (its reports and messages, oldest
// first, a composer, and Apply / Reject / Revert for a proposed "How I work" change).
import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  CompanyTile,
  cn,
  EmptyState,
  formatMicroMoney,
  InfoTip,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  NavList,
  NavRow,
  SidebarCaption,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  toast,
  WorkerBadge,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BotIcon,
  CheckIcon,
  HeartPulseIcon,
  LightbulbIcon,
  MessageSquareIcon,
  SendIcon,
  SunriseIcon,
  Undo2Icon,
  WrenchIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import {
  type AgentHealthFilters,
  agentHealthQuery,
  agentHealthTopicQuery,
  opportunitiesQuery,
  opsKeys,
} from "../ops-queries";
import { OpsBody, OpsFrame, operatorCrumb, RowAction, RowLinkAction, RowSub } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { OpportunitiesView } from "./opportunities";

type Health = OperationOutput<"operator.agentHealth.get">;
type Topic = OperationOutput<"operator.agentHealth.topic">;
type Report = OperationOutput<"operator.agentHealth.review">;
type HubCompany = Health["companies"][number]["company"];
type Budget = Health["companies"][number]["budget"];
export type Severity = Report["severity"];

export const SEVERITIES = ["critical", "high", "medium", "low"] as const;

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** Critical red, high orange, medium neutral, low muted. */
export const SEVERITY_BADGE = {
  critical: "danger",
  high: "warning",
  medium: "neutral",
  low: "outline",
} as const satisfies Record<Severity, string>;

const FIX_LABEL: Record<Report["fix_status"], string | null> = {
  none: null,
  proposed: "Fix proposed",
  applied: "Applied",
  rejected: "Rejected",
  reverted: "Reverted",
};

const FIX_BADGE = {
  none: "neutral",
  proposed: "info",
  applied: "success",
  rejected: "neutral",
  reverted: "neutral",
} as const satisfies Record<Report["fix_status"], string>;

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const label = (s: string) => {
  const t = s.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** The platform budget's day: "$0.40 / $2.00 today", Paused when stopped. */
function BudgetLine({ budget }: { budget: Budget }) {
  if (!budget) return null;
  return (
    <span className="inline-flex items-center gap-1.5" data-slot="skillsmith-budget">
      <span className="tabular-nums">
        {formatMicroMoney(budget.spent_micros, "USD")} /{" "}
        {formatMicroMoney(budget.cap_micros, "USD")} today
      </span>
      {budget.paused ? <Badge variant="danger">Paused</Badge> : null}
    </span>
  );
}

const isNotFound = (e: unknown) => e instanceof ApiError && e.status === 404;

function SuperAdminsOnly() {
  return (
    <EmptyState
      variant="card"
      icon={HeartPulseIcon}
      title="Super admins only"
      description="Agent health is open to platform super admins."
    />
  );
}

/**
 * The hub's frame: header, the topic list, and the main pane (the morning view, one company's
 * topic, or — SKILLSMITH-OPPORTUNITIES — the helper sales pipeline, optionally filtered).
 */
export function AgentHealthHub({
  api,
  companyId,
  view,
}: {
  api: Api;
  companyId?: string;
  view?: "opportunities";
}) {
  const list = useQuery(agentHealthQuery(api));
  const topicId = view ? undefined : companyId;
  const topic = useQuery({
    ...agentHealthTopicQuery(api, topicId ?? ""),
    enabled: Boolean(topicId),
  });
  const company =
    topic.data?.company ?? list.data?.companies.find((c) => c.company.id === topicId)?.company;
  const agentName = topic.data?.agents[0]?.name ?? "Skillsmith";
  const hubCrumb = { label: "Agent health", render: <Link to="/operator/agent-health" /> };
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={
          view
            ? [operatorCrumb, hubCrumb, { label: "Opportunities" }]
            : topicId
              ? [operatorCrumb, hubCrumb, { label: company?.name ?? "Company" }]
              : [operatorCrumb, { label: "Agent health" }]
        }
        icon={view ? LightbulbIcon : HeartPulseIcon}
        title={
          view
            ? "Opportunities"
            : topicId
              ? `${company?.name ?? "…"} · ${agentName}`
              : "Agent health"
        }
        actions={<Badge variant="outline">Super admins</Badge>}
      />
      <div className="flex min-h-0 min-w-0 flex-1 max-md:flex-col">
        <aside
          aria-label="Topics"
          data-slot="agent-health-topics"
          className="w-60 shrink-0 border-r border-subtle px-2 py-3 max-md:w-full max-md:border-r-0 max-md:border-b"
        >
          <NavList>
            <NavRow
              icon={SunriseIcon}
              label="Since yesterday"
              render={
                <Link
                  to="/operator/agent-health"
                  activeOptions={{ exact: true, includeSearch: false }}
                />
              }
            />
            <NavRow
              icon={LightbulbIcon}
              label="Opportunities"
              render={
                <Link
                  to="/operator/agent-health/opportunities"
                  activeOptions={{ includeSearch: false }}
                />
              }
            />
          </NavList>
          {list.data?.companies.length ? (
            <>
              <SidebarCaption label="Companies" />
              <NavList>
                {list.data.companies.map((c) => (
                  <NavRow
                    key={c.company.id}
                    data-company={c.company.id}
                    label={c.company.name}
                    lead={<CompanyTile name={c.company.name} size={20} />}
                    count={c.unreviewed}
                    badge={
                      c.counts.critical > 0 ? (
                        <span
                          role="img"
                          aria-label="Critical"
                          className="size-2 shrink-0 rounded-full bg-destructive"
                        />
                      ) : null
                    }
                    render={
                      <Link
                        to="/operator/agent-health/$companyId"
                        params={{ companyId: c.company.id }}
                      />
                    }
                  />
                ))}
              </NavList>
            </>
          ) : null}
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          {view ? (
            <OpportunitiesView
              key={companyId ?? ""}
              api={api}
              {...(companyId ? { companyId } : {})}
            />
          ) : topicId ? (
            // Keyed by company: a draft never follows you into another company's topic.
            <TopicView key={topicId} api={api} companyId={topicId} />
          ) : (
            <MorningView api={api} />
          )}
        </div>
      </div>
    </OpsFrame>
  );
}

function useReportActions(api: Api) {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: opsKeys.agentHealth() });
  const review = useMutation({
    mutationFn: (v: { report: Report; reviewed: boolean }) =>
      api.client.operator.agentHealth.review({
        params: { company_id: v.report.company_id, report_id: v.report.id },
        body: { reviewed: v.reviewed },
      }),
    onSuccess: (_r, v) => {
      if (v.reviewed)
        toast.add({
          title: "Marked reviewed",
          description: v.report.title,
          actionProps: {
            children: "Undo",
            onClick: () => review.mutate({ report: v.report, reviewed: false }),
          },
        });
    },
    onSettled: refresh,
  });
  return { review };
}

/** "Since yesterday": every granted company's new reports, critical first, then company. */
function MorningView({ api }: { api: Api }) {
  const [severities, setSeverities] = useState<Severity[]>([]);
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<"unreviewed" | "all">("unreviewed");
  const filters: AgentHealthFilters = {
    status,
    ...(severities.length
      ? { severity: SEVERITIES.filter((s) => severities.includes(s)).join(",") }
      : {}),
    ...(company ? { company_id: company } : {}),
  };
  const q = useQuery(agentHealthQuery(api, filters));
  const { review } = useReportActions(api);
  if (q.isPending)
    return (
      <OpsBody>
        <Loading label="Loading agent health" />
      </OpsBody>
    );
  if (q.isError)
    return (
      <OpsBody>
        {isNotFound(q.error) ? (
          <SuperAdminsOnly />
        ) : (
          <LoadError what="Agent health" error={q.error} />
        )}
      </OpsBody>
    );
  const h = q.data;
  return (
    <OpsBody data-slot="agent-health-morning">
      <Inbox inbox={h.inbox} />
      <section
        data-slot="severity-counts"
        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"
        aria-label="New reports by severity"
      >
        <span className="font-medium">Since yesterday</span>
        {SEVERITIES.map((s) => (
          <span key={s} data-severity={s} className="inline-flex items-center gap-1.5">
            <Badge variant={SEVERITY_BADGE[s]} dot={s === "critical"}>
              {SEVERITY_LABEL[s]}
            </Badge>
            <span className="tabular-nums" data-slot="severity-count">
              {h.totals[s]}
            </span>
          </span>
        ))}
      </section>
      {h.companies.length ? <CompanyLines companies={h.companies} /> : null}
      <div
        role="toolbar"
        aria-label="Filters"
        className="flex flex-wrap items-center gap-2"
        data-slot="agent-health-filters"
      >
        <ToggleGroup
          variant="chip"
          multiple
          value={severities}
          onValueChange={(v: unknown[]) => setSeverities(v as Severity[])}
          aria-label="Severity"
        >
          {SEVERITIES.map((s) => (
            <ToggleGroupItem key={s} value={s}>
              {SEVERITY_LABEL[s]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <InfoTip label="Severity criteria">
          <ul className="flex flex-col gap-1" data-slot="severity-criteria">
            {SEVERITIES.map((s) => (
              <li key={s}>
                <span className="font-medium text-foreground">{SEVERITY_LABEL[s]}</span> —{" "}
                {h.criteria[s]}
              </li>
            ))}
          </ul>
        </InfoTip>
        <NativeSelect
          size="sm"
          aria-label="Company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        >
          <NativeSelectOption value="">All companies</NativeSelectOption>
          {h.companies.map((c) => (
            <NativeSelectOption key={c.company.id} value={c.company.id}>
              {c.company.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <ToggleGroup
          variant="chip"
          value={[status]}
          onValueChange={(v: unknown[]) => {
            const next = v[0] as "unreviewed" | "all" | undefined;
            if (next) setStatus(next);
          }}
          aria-label="Review status"
        >
          <ToggleGroupItem value="unreviewed">Unreviewed</ToggleGroupItem>
          <ToggleGroupItem value="all">All</ToggleGroupItem>
        </ToggleGroup>
      </div>
      {h.reports.length === 0 ? (
        <EmptyState
          variant="card"
          icon={HeartPulseIcon}
          title={status === "unreviewed" ? "Nothing new to review" : "No new reports"}
          description="Skillsmith's reports since yesterday appear here."
        />
      ) : (
        <ol className="flex flex-col divide-y divide-subtle" data-slot="agent-health-reports">
          {h.reports.map((r) => (
            <li key={r.id} className="py-3 first:pt-0">
              <ReportCard
                report={r}
                company={r.company}
                actions={
                  <>
                    <ReviewAction
                      report={r}
                      pending={review.isPending}
                      onReview={(reviewed) => review.mutate({ report: r, reviewed })}
                    />
                    <RowLinkAction
                      icon={MessageSquareIcon}
                      label={`Open ${r.company.name}'s topic`}
                      tooltip="Open topic"
                      render={
                        <Link
                          to="/operator/agent-health/$companyId"
                          params={{ companyId: r.company.id }}
                        />
                      }
                    />
                  </>
                }
              />
            </li>
          ))}
        </ol>
      )}
    </OpsBody>
  );
}

/** The super admins' inbox: open Critical reports and paused agents, a slim strip. */
function Inbox({ inbox }: { inbox: Health["inbox"] }) {
  if (inbox.length === 0) return null;
  return (
    <ul
      aria-label="Inbox"
      data-slot="agent-health-inbox"
      className="flex flex-col gap-1 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger-text"
    >
      {inbox.map((i) => (
        <li key={i.id} className="flex min-w-0 items-center gap-2">
          <span className="font-medium">
            {i.kind === "paused"
              ? "Paused"
              : i.kind === "memory_quality"
                ? `Memory · ${SEVERITY_LABEL[i.severity]}`
                : "Critical"}
          </span>
          {i.kind === "memory_quality" ? (
            <Link
              to="/operator/memory-quality/$companyId"
              params={{ companyId: i.company.id }}
              className="min-w-0 truncate underline-offset-4 hover:underline"
            >
              {i.company.name} · {i.title}
            </Link>
          ) : (
            <Link
              to="/operator/agent-health/$companyId"
              params={{ companyId: i.company.id }}
              className="min-w-0 truncate underline-offset-4 hover:underline"
            >
              {i.company.name} · {i.title}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

/** One line per company: new reports by severity, unreviewed, open issues, Skillsmith's budget. */
function CompanyLines({ companies }: { companies: Health["companies"] }) {
  return (
    <ul className="flex flex-col gap-1 text-sm" aria-label="Companies" data-slot="company-lines">
      {companies.map((c) => (
        <li
          key={c.company.id}
          data-company={c.company.id}
          className="flex flex-wrap items-center gap-x-3 gap-y-1"
        >
          <Link
            to="/operator/agent-health/$companyId"
            params={{ companyId: c.company.id }}
            className="font-medium underline-offset-4 hover:underline"
          >
            {c.company.name}
          </Link>
          <span className="text-muted-foreground tabular-nums">
            {SEVERITIES.filter((s) => c.counts[s] > 0)
              .map((s) => `${c.counts[s]} ${SEVERITY_LABEL[s].toLowerCase()}`)
              .join(" · ") || "0 new"}
          </span>
          {c.unreviewed ? (
            <span className="text-muted-foreground tabular-nums">{c.unreviewed} unreviewed</span>
          ) : null}
          <span className="text-muted-foreground tabular-nums">
            {c.open_issues} open {c.open_issues === 1 ? "issue" : "issues"}
          </span>
          <span className="text-muted-foreground">
            <BudgetLine budget={c.budget} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReviewAction({
  report,
  pending,
  onReview,
}: {
  report: Report;
  pending: boolean;
  onReview: (reviewed: boolean) => void;
}) {
  return report.reviewed_at ? (
    <RowAction
      icon={Undo2Icon}
      label={`Mark “${report.title}” unreviewed`}
      tooltip="Mark unreviewed"
      disabled={pending}
      onClick={() => onReview(false)}
    />
  ) : (
    <RowAction
      icon={CheckIcon}
      label={`Mark “${report.title}” reviewed`}
      tooltip="Mark reviewed"
      disabled={pending}
      onClick={() => onReview(true)}
    />
  );
}

/** A report: severity, company, category, title, agents, evidence, suggested fix, fix status. */
function ReportCard({
  report: r,
  company,
  actions,
  children,
}: {
  report: Report;
  company?: HubCompany;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const fix = FIX_LABEL[r.fix_status];
  return (
    <article
      className="flex min-w-0 flex-col gap-1.5"
      data-slot="agent-health-report"
      data-report={r.id}
      data-severity={r.severity}
      data-reviewed={r.reviewed_at ? "true" : "false"}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Badge variant={SEVERITY_BADGE[r.severity]} dot={r.severity === "critical"}>
            {SEVERITY_LABEL[r.severity]}
          </Badge>
          {company ? <span className="text-sm font-medium">{company.name}</span> : null}
          <span className="text-xs text-muted-foreground">{label(r.kind)}</span>
          {fix ? <Badge variant={FIX_BADGE[r.fix_status]}>{fix}</Badge> : null}
          {r.reviewed_at ? <Badge variant="outline">Reviewed</Badge> : null}
          <RowSub>{when(r.created_at)}</RowSub>
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-0.5">{actions}</div> : null}
      </div>
      <h3 className="text-sm font-semibold">{r.title}</h3>
      {r.body ? <p className="whitespace-pre-wrap text-sm text-secondary">{r.body}</p> : null}
      {r.affected_agents.length ? (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <BotIcon aria-hidden="true" className="size-3.5" />
          <span className="sr-only">Affected agents:</span>
          {r.affected_agents.join(", ")}
        </div>
      ) : null}
      {r.evidence.length ? (
        <ul className="flex flex-wrap gap-1" aria-label="Evidence">
          {r.evidence.map((e) => (
            <li key={`${e.kind}:${e.id}`}>
              <Badge variant="outline" title={e.note ? `${e.id} — ${e.note}` : e.id}>
                {e.kind} {e.id.slice(0, 8)}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {r.suggested_fix ? (
        <div className="flex items-start gap-1.5 text-sm">
          <WrenchIcon
            aria-hidden="true"
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
          />
          <span className="sr-only">Suggested fix:</span>
          <span className="whitespace-pre-wrap">{r.suggested_fix}</span>
        </div>
      ) : null}
      {children}
    </article>
  );
}

/** A report's "How I work" proposal: the new text, Apply / Reject while proposed, Revert once applied. */
function ProposalBlock({ api, report: r }: { api: Api; report: Report }) {
  const client = useQueryClient();
  const [confirm, setConfirm] = useState<"apply" | "revert" | null>(null);
  const fix = useMutation({
    mutationFn: (action: "apply" | "reject" | "revert") =>
      api.client.operator.agentHealth.fix({
        params: { company_id: r.company_id, report_id: r.id },
        body: { action },
      }),
    onSettled: () => client.invalidateQueries({ queryKey: opsKeys.agentHealth() }),
  });
  const p = r.proposal;
  if (!p) return null;
  const error =
    fix.error instanceof ApiError
      ? (fix.error.problem?.detail ?? fix.error.problem?.title ?? fix.error.message)
      : fix.error
        ? fix.error.message
        : null;
  return (
    <section
      aria-label="Proposed change"
      data-slot="agent-health-proposal"
      className="mt-1 flex flex-col gap-2 rounded-md border border-subtle p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Proposed change</span>
        <span className="text-xs text-muted-foreground">
          New How I work (from v{p.base_version})
        </span>
      </div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-sm bg-sunken p-2 font-mono text-xs">
        {p.text}
      </pre>
      <div className="flex flex-wrap items-center gap-2">
        {r.fix_status === "proposed" ? (
          <>
            <Button size="sm" disabled={fix.isPending} onClick={() => setConfirm("apply")}>
              <CheckIcon aria-hidden="true" />
              Apply
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={fix.isPending}
              onClick={() => fix.mutate("reject")}
            >
              Reject
            </Button>
          </>
        ) : r.fix_status === "applied" ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={fix.isPending}
            onClick={() => setConfirm("revert")}
          >
            <Undo2Icon aria-hidden="true" />
            Revert
          </Button>
        ) : FIX_LABEL[r.fix_status] ? (
          <Badge variant={FIX_BADGE[r.fix_status]}>{FIX_LABEL[r.fix_status]}</Badge>
        ) : null}
        {error ? (
          <span role="alert" className="text-sm text-danger-text">
            {error}
          </span>
        ) : null}
      </div>
      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "revert" ? `Revert to v${p.base_version}?` : `Apply the new How I work?`}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={confirm === "revert" ? "destructive" : "default"}
              onClick={() => {
                if (confirm) fix.mutate(confirm);
                setConfirm(null);
              }}
            >
              {confirm === "revert" ? "Revert" : "Apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/** SKILLSMITH-OPPORTUNITIES: "Opportunities (n open)" → the pipeline filtered to the company. */
function OpenOpportunities({ api, companyId }: { api: Api; companyId: string }) {
  const q = useQuery(opportunitiesQuery(api, { company_id: companyId }));
  const c = q.data?.pipeline.counts;
  if (!c) return null;
  const open = c.suggested + c.offered + c.accepted;
  return (
    <Link
      to="/operator/agent-health/opportunities"
      search={{ company: companyId }}
      className="text-primary underline-offset-4 hover:underline"
      data-slot="topic-opportunities"
    >
      Opportunities ({open} open)
    </Link>
  );
}

/** One company's topic: reports and messages, oldest first, and a composer. */
function TopicView({ api, companyId }: { api: Api; companyId: string }) {
  const q = useQuery(agentHealthTopicQuery(api, companyId));
  const client = useQueryClient();
  const { review } = useReportActions(api);
  const [draft, setDraft] = useState("");
  const send = useMutation({
    mutationFn: (body: string) =>
      api.client.operator.agentHealth.message({
        params: { company_id: companyId },
        body: { body },
      }),
    onSuccess: () => {
      setDraft("");
      void client.invalidateQueries({ queryKey: opsKeys.agentHealthTopic(companyId) });
    },
  });
  if (q.isPending)
    return (
      <OpsBody>
        <Loading label="Loading the topic" />
      </OpsBody>
    );
  if (q.isError)
    return (
      <OpsBody>
        {isNotFound(q.error) ? <SuperAdminsOnly /> : <LoadError what="The topic" error={q.error} />}
      </OpsBody>
    );
  const t: Topic = q.data;
  const agentName = t.agents[0]?.name ?? "Skillsmith";
  const submit = () => {
    if (draft.trim()) send.mutate(draft.trim());
  };
  return (
    <OpsBody data-slot="agent-health-topic">
      <RowSub className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>{t.company.name}</span>
        <span className="tabular-nums">
          {t.open_issues} open {t.open_issues === 1 ? "issue" : "issues"}
        </span>
        {t.pending ? <span className="tabular-nums">{t.pending} waiting for an answer</span> : null}
        <BudgetLine budget={t.budget} />
        <OpenOpportunities api={api} companyId={companyId} />
      </RowSub>
      {t.items.length === 0 ? (
        <EmptyState
          variant="card"
          icon={HeartPulseIcon}
          title="No reports or messages yet"
          description={`Ask ${agentName} below.`}
        />
      ) : (
        <ol className="flex flex-col gap-4" data-slot="agent-health-timeline">
          {t.items.map((item) =>
            item.type === "report" ? (
              <li key={`r:${item.report.id}`} className="rounded-md border border-subtle p-3">
                <ReportCard
                  report={item.report}
                  actions={
                    <ReviewAction
                      report={item.report}
                      pending={review.isPending}
                      onReview={(reviewed) => review.mutate({ report: item.report, reviewed })}
                    />
                  }
                >
                  {item.report.proposal ? <ProposalBlock api={api} report={item.report} /> : null}
                </ReportCard>
              </li>
            ) : (
              <li
                key={`m:${item.message.id}`}
                className="flex flex-col gap-1"
                data-author={item.message.author_kind}
              >
                <div className="flex items-center gap-2">
                  <WorkerBadge
                    kind={item.message.author_kind === "system_agent" ? "system" : "person"}
                    size="sm"
                  />
                  <span className="text-sm font-medium">
                    {item.message.author_kind === "system_agent" ? agentName : "Super admin"}
                  </span>
                  {item.message.kind !== "message" ? (
                    <Badge variant="outline">{label(item.message.kind)}</Badge>
                  ) : null}
                  <RowSub>{when(item.message.created_at)}</RowSub>
                </div>
                <p className="whitespace-pre-wrap text-sm">{item.message.body}</p>
              </li>
            ),
          )}
        </ol>
      )}
      <form
        className={cn("mt-auto flex items-end gap-2 border-t border-subtle pt-3")}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Textarea
          aria-label={`Message ${agentName}`}
          placeholder={`Ask ${agentName}…`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          className="min-h-10 flex-1"
        />
        <Button
          type="submit"
          size="icon"
          aria-label="Send"
          disabled={!draft.trim() || send.isPending || t.agents.length === 0}
        >
          <SendIcon aria-hidden="true" />
        </Button>
      </form>
      {send.error ? (
        <span role="alert" className="text-sm text-danger-text">
          {send.error instanceof ApiError
            ? (send.error.problem?.detail ?? send.error.message)
            : send.error.message}
        </span>
      ) : null}
    </OpsBody>
  );
}
